import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, colors, InlineLink, SectionAlert, StandAloneLink, Surface, Txt } from '@vst/beam';
import { Offer, OpenInNew } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { MyAddonsCard, MyAddonsError } from '@app/components/MyAddonsCard';
import { useAccountStatus, useNavigate, useScreenResolution, useShowAddonsShop } from '@app/hooks';
import {
  getFormattedDiscountDate,
  openInNewTab,
  selectAddOns,
  selectHasAddons,
  selectServiceRegion,
  selectUserInfo,
} from '@app/redux';
import { selectConfig } from '@app/redux/config';

import { AccountType, GetCurrentAddOnsPayload } from '@mfe/shared/schema-types';

import { AddonManagementModal } from '../RemoveAddon/AddonManagementModal';
import { ADD_ON_STATE } from '../../../../constants';

import styles from './Addons.module.scss';

export const AddonsCard = () => {
  const { t } = useTranslation(['Profile', 'Global']);
  const { goTo } = useNavigate();
  const { currentAddOns, error, loading: currentAddOnsLoading } = useSelector(selectAddOns);
  const { isAccountPreInstall } = useAccountStatus();

  const dispatch = useDispatch();

  const { isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;

  const showAddonsShop = useShowAddonsShop();

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const hasAddOns = useSelector(selectHasAddons);

  const openLinkInNewTab = (e: React.MouseEvent<HTMLElement>, url: string) => {
    e.preventDefault();
    dispatch(openInNewTab(url));
  };

  const addOnsLink =
    accountType === AccountType.Residential ? t('Global:addOnsResidentialLink') : t('Global:addOnsSMBLink');

  if (!showAddonsShop && !hasAddOns) {
    return null;
  }

  if (currentAddOnsLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <MyAddonsError
        title={t('Profile:addOns.title')}
        description={t('Profile:addOns.error.description')}
        refreshButtonLabel={t('Profile:addOns.error.refreshButton')}
      />
    );
  }

  return (
    <div className={styles['container']}>
      <Surface variant="primary" radius={isMobile ? '0px' : '16px'}>
        {hasAddOns ? (
          <>
            <div className={styles['header']}>
              <Txt variant="heading5">{t('Profile:addOns.title')}</Txt>
              {isAccountPreInstall ? (
                <></>
              ) : showAddonsShop ? (
                <Button onClick={() => goTo('ShopAddons')} variant="secondary" buttonSize="small">
                  {t('Profile:addOns.shopAddonsButtonLabel')}
                </Button>
              ) : (
                <InlineLink
                  href={addOnsLink}
                  openInNewLocation={true}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    openLinkInNewTab(e, addOnsLink);
                  }}
                >
                  <Button variant="secondary" icon={OpenInNew} iconPos="right">
                    {t('Profile:addOns.seeMoreButton')}
                  </Button>
                </InlineLink>
              )}
            </div>
            <div className={styles['addons']}>
              {currentAddOns?.map((addon: any, index: number) => (
                <AddonWithModal addon={addon} key={index} />
              ))}
            </div>
          </>
        ) : (
          <NoAddons />
        )}
      </Surface>
    </div>
  );
};

const AddonWithModal = ({ addon }: { addon: GetCurrentAddOnsPayload }) => {
  const { description, price, priceWithDiscount, discountName, discountEndDate, state, name, isBundle } = addon;

  const { t } = useTranslation(['Profile', 'Global']);
  const { unrestrictedRemovalProducts, disconnectRegions, restrictAddonRemoval } = useSelector(selectConfig);
  const pendingDeactivation = state.includes(ADD_ON_STATE.PENDING);

  const serviceRegion = useSelector(selectServiceRegion);
  const isRemovalAllowed = disconnectRegions.includes(serviceRegion) && !isBundle; // Allow disconnect only in approved states AND if it's not a bundle add-on
  const isRemovalUnrestricted = unrestrictedRemovalProducts.includes(name); // Allow disconnection of specific products in all states
  const canRemoveAddon = restrictAddonRemoval ? isRemovalUnrestricted || isRemovalAllowed : true;

  const showManageAddonLink = canRemoveAddon && !pendingDeactivation;

  return (
    <div className={styles['addons__item']}>
      <MyAddonsCard addon={addon}>
        {description && (
          <Txt variant="smallRegular" color="regular">
            {description}
          </Txt>
        )}
        <div className={styles['pricesAndDiscounts']}>
          {discountName ? (
            <DiscountedPrice addOnPrice={price} priceWithDiscount={priceWithDiscount} />
          ) : (
            <Txt variant="bodySmallBold" color={Number(price) > 0 ? 'subtle' : 'success'}>
              {Number(price) > 0 ? t('addOns.price', { addOnPrice: price }) : t('addOns.freePrice')}
            </Txt>
          )}
          {showManageAddonLink && <ManageLink addon={addon} />}
        </div>
        {discountName && discountEndDate && (
          <SectionAlert variant="success" icon={Offer}>
            <Txt variant="smallRegular" color="regular">
              {t('Profile:addOns.offerAlert', {
                discountName,
                discountEndDate: discountEndDate ? getFormattedDiscountDate(discountEndDate) : '',
                addOnPrice: price,
              })}
            </Txt>
          </SectionAlert>
        )}
      </MyAddonsCard>
    </div>
  );
};

const ManageLink = ({ addon }: { addon: GetCurrentAddOnsPayload }) => {
  const { productInstanceId, name, isBundle, isRescindable } = addon;
  const { t } = useTranslation('Profile');
  const { modalType } = useSelector((state: { addOns: any }) => state.addOns);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      <StandAloneLink
        variant="primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleOpenModal();
        }}
        ml="4px"
        style={{ textDecoration: 'none', display: 'inline-block' }}
      >
        {t('manage.link')}
      </StandAloneLink>
      <AddonManagementModal
        id={productInstanceId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        addOnName={name}
        isBundleAddOn={isBundle}
        modalType={modalType}
        isRescindable={isRescindable}
      />
    </>
  );
};

type DiscountedPricesProps = {
  addOnPrice: string;
  priceWithDiscount: number | undefined | null;
};

const DiscountedPrice = ({ addOnPrice, priceWithDiscount }: DiscountedPricesProps) => {
  const { t } = useTranslation('Profile');
  return (
    <div>
      {priceWithDiscount && priceWithDiscount > 0 ? (
        <Txt variant="bodySmallBold" color="subtle" component="span" mr="4px">
          {t('addOns.priceWithDiscount', {
            discountPrice: priceWithDiscount.toFixed(2),
          })}
        </Txt>
      ) : (
        <Txt variant="bodySmallBold" color="subtle" component="span" mr="4px" style={{ color: colors['green'][700] }}>
          {t('addOns.freePrice')}
        </Txt>
      )}
      <Txt variant="bodySmallRegular" component="span">
        <s>{t('addOns.price', { addOnPrice })}</s>
      </Txt>
    </div>
  );
};

const NoAddons = () => {
  const { t } = useTranslation(['Profile', 'Overview']);
  const { goTo } = useNavigate();

  const { isSmall, isExtraSmall } = useScreenResolution();

  const isMobile = isSmall || isExtraSmall;
  return (
    <Surface variant="primary" radius={isMobile ? '0px' : '16px'}>
      <div className={styles['header']}>
        <Txt variant="heading5">{t('Profile:addOns.title')}</Txt>
      </div>
      <div className={styles['no-add-ons-container']}>
        <Txt variant="bodySmallRegular" color="subtle">
          {t('Profile:addOns.noAddOns')}
        </Txt>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.preventDefault();
            goTo('ShopAddons');
          }}
        >
          {t('Overview:addOns.shopAddons')}
        </Button>
      </div>
    </Surface>
  );
};

const Loading = () => {
  return (
    <Surface data-cy="loading" className={styles['profile-loading-container']}>
      <div className={styles['loading-title-container']}>
        <div className={styles['loading-title']} />
      </div>

      <div className={styles['loading-content']}>
        <div className={styles['icon']} />
        <div className={styles['text-content']}>
          <div className={styles['title']} />
          <div className={styles['desc']} />
          <div className={styles['price']} />
        </div>
      </div>

      <div className={styles['loading-content']}>
        <div className={styles['icon']} />
        <div className={styles['text-content']}>
          <div className={styles['title']} />
          <div className={styles['desc']} />
          <div className={styles['price']} />
        </div>
      </div>
    </Surface>
  );
};
